import axios from 'axios';
import urls from './Urls';
import { action, makeObservable, observable, runInAction } from "mobx";
import additionalInfo from './AdditionalInfo';
import dictionary from './Dictionary';
import { replacePlaceholders } from "../Utils/additionalInfo";

class AdditionalInfo_parallelCreation {
    isCanceled = false;

    uiStatus = {
        totalWords: 0,
        completedWords: 0,
        totalProcesses: 0,
        perProcess: [], // { wordsCount, completedCount, message }
    };

    constructor() {
        makeObservable(this, {
            isCanceled: observable,
            uiStatus: observable,
            createMultipleProcesses: action,
            cancelCreation: action,
        });
    }

    async createMultipleProcesses(type, quantity, delayTime = 1000, withDescription, numOfProcesses) {
        this.isCanceled = false;

        // dictionary
        const dictionaryType = dictionary.current.dictionaryType;
        const fullDictionary = dictionary.current.data[dictionaryType];

        // validation
        if (!Array.isArray(fullDictionary) || typeof numOfProcesses !== 'number' || numOfProcesses <= 0) {
            throw new Error('Invalid input');
        }

        // Split the fullDictionary into chunks
        const chunkSize = Math.ceil(fullDictionary.length / numOfProcesses);

        this.uiStatus.totalWords = fullDictionary.length;
        this.uiStatus.completedWords = 0;
        this.uiStatus.totalProcesses = numOfProcesses;
        this.uiStatus.perProcess = [];

        const promises = [];

        for (let i = 0; i < numOfProcesses; i++) {
            const start = i * chunkSize;
            const end = start + chunkSize;
            const chunk = fullDictionary.slice(start, end);

            this.uiStatus.perProcess.push({
                wordsCount: chunk.length,
                completedCount: 0,
                message: { type: "success", text: `מתחיל ליצור ${type} עבור ${chunk.length} מילים` }
            });

            const promise = this.createOneProcess(i, type, quantity, delayTime, withDescription, dictionaryType, chunk);
            promises.push(promise);
        }

        await Promise.all(promises);
        this.cancelCreation();
    }

    async createOneProcess(processIndex, type, quantity, delayTime, withDescription, dictionaryType, dictionaryChunk) {
        await this.delay(delayTime);

        for (const currentWord of dictionaryChunk) {
            if (this.isCanceled) break;
            await this.delay(delayTime > 2000 ? delayTime : 2000);
            if (this.isCanceled) break;

            for (let i = 0; i < quantity; i++) {
                if (this.isCanceled) break;

                // Create the new info ----------------------------------------------
                let newInfo = null;
                if (type === "image") {
                    const description = withDescription
                        ? await this.createInfo("imageDescription", null, currentWord, processIndex)
                        : null;
                    if (this.isCanceled) break;
                    newInfo = await this.createInfo(type, description, currentWord, processIndex);
                } else {
                    newInfo = await this.createInfo(type, null, currentWord, processIndex);
                }

                await this.delay(delayTime);
                if (this.isCanceled) break;

                // Save the new info ------------------------------------------------
                if (!newInfo) continue;

                const saveMap = {
                    image: "image",
                    sentence: "sentence",
                    definition: "definition",
                    story: "story",
                    trnsAr: "trnsAr",
                    trnsRu: "trnsRu",
                };

                if (saveMap[type]) {
                    await this.saveInfo({
                        fields: { [saveMap[type]]: newInfo },
                        currentWord,
                        dictionaryType,
                        processIndex
                    });
                } else {
                    await this.saveArrayOfInfo(type, newInfo, currentWord, dictionaryType, processIndex);
                }

                runInAction(() => {
                    this.uiStatus.perProcess[processIndex].message = {
                        type: "success",
                        text: `נשמר ${i + 1} ${type} עבור ${currentWord.original}`
                    };
                });
            }

            runInAction(() => {
                this.uiStatus.perProcess[processIndex].completedCount += 1;
                this.uiStatus.completedWords += 1;
            });

            await this.delay(delayTime);
        }
    }

    async createInfo(type, prompt = null, currentWord, processIndex) {
        try {
            // prompt
            if (!prompt) {
                // Get the selected prompt for the type
                // console.log(toJS(this.aiSettings.text.prompts.options[type]));
                prompt = additionalInfo.aiSettings.text.prompts.options[type]
                    .find(p => p._id === additionalInfo.aiSettings.text.prompts.selected[type])?.prompt;

                // Replace placeholders in the prompt with values from the current word
                prompt = replacePlaceholders(prompt, currentWord);
            }

            // request
            const response = await axios.post(urls.ai.create, {
                type,
                prompt,
                provider: additionalInfo.aiSettings.text.providers.selected,
                model: additionalInfo.aiSettings.text.models[additionalInfo.aiSettings.text.providers.selected].selected,
                temperature: additionalInfo.aiSettings.text.temperature
            }, {
                withCredentials: true
            });
            const result = response.data.result;

            let newInfo = null;

            const isArray = [
                "distractingForTrns",
                "distractingForOriginal",
                "distractingForDefinitionInEng",
                "distractingForDefinitionInHeb",
                "distractingForWordFamilies",
                "sentencesWithMissing",
                "sentencesWithLastMissing",
                "opposites",
                "synonyms"
            ].includes(type);

            if (isArray) {
                const match = result.match(/\[[^\]]*\]/);
                const arr = match ? JSON.parse(match[0]) : [];
                newInfo = [];
                for (const text of arr)
                    newInfo.push(text);
            } else {
                newInfo = result;
            }

            // message
            const model = response.data.model;
            const elapsedTime = response.data.elapsedTime;
            runInAction(() => {
                if (type === "image") {
                    this.uiStatus.perProcess[processIndex].message = { type: "success", text: `נוצר ${type}: מודל: ${model}, זמן: ${elapsedTime} שניות.` };
                }
                else {
                    let temperature = response.data.temperature;
                    if (["o1", "o3-mini"].includes(model)) temperature = "אינו נתמך בדגם זה";
                    const { input, output, total } = response.data.tokens;
                    this.uiStatus.perProcess[processIndex].message = {
                        type: "success", text: `
                    נוצר ${type}: מודל: ${model}, טמפרטורה: ${temperature}, זמן: ${elapsedTime} שניות.
                    טוקנים שאלה: ${input}, טוקנים תשובה: ${output}, סך הכל: ${total} טוקנים.`
                    }
                }
            });
            return newInfo;
        } catch (error) {
            runInAction(() => {
                this.uiStatus.perProcess[processIndex].message = {
                    type: "error",
                    text: `Error creating ${type} - ${error} - ${error?.response?.data}`
                        .replace(/&quot;/g, '"').replace(/&amp;/g, '&')
                };
            });
            return null;
        }
    }

    async saveInfo({ fields = {}, currentWord, dictionaryType, processIndex }) {
        try {
            const wordId = currentWord._id;
            let imageUrl = typeof fields.image === "string" && fields.image.startsWith("http") ? fields.image : null;

            const newAdditionalInfo = {
                word: { dictionaryType, _id: wordId },
                images: { fileName: imageUrl },
                sentences: { text: fields.sentence },
                definitions: { text: fields.definition },
                stories: { text: fields.story },
                distractingForTrns: { text: fields.distrForTrns },
                distractingForOriginal: { text: fields.distrForOriginal },
                distractingForDefinitionInEng: { text: fields.distrForDefinitionInEng },
                distractingForDefinitionInHeb: { text: fields.distrForDefinitionInHeb },
                distractingForWordFamilies: { text: fields.distrForWordFamilies },
                sentencesWithMissing: { text: fields.sentencesMissing },
                sentencesWithLastMissing: { text: fields.sentencesLastMissing },
                opposites: { text: fields.opposite },
                synonyms: { text: fields.synonym },
                trnsAr: { text: fields.trnsAr },
                trnsRu: { text: fields.trnsRu },
            };

            // Remove undefined or null fields
            Object.keys(newAdditionalInfo).forEach(key => {
                if (
                    newAdditionalInfo[key] == null ||
                    (typeof newAdditionalInfo[key] === 'object' &&
                    Object.values(newAdditionalInfo[key])[0] == null)
                ) {
                    delete newAdditionalInfo[key];
                }
            });

            const response = await axios.patch(urls.additionalInfo.add, newAdditionalInfo, {
                withCredentials: true
            });

            // Update the dictionary with the new additional info
            // const updatedAdditionalInfo = response.data.updatedAdditionalInfo;
            dictionary.updateAdditionalInfo(currentWord);

            if (fields.trnsAr)
                dictionary.updateCurrentFieldLocally("trnsAr", fields.trnsAr, currentWord);
            if (fields.trnsRu)
                dictionary.updateCurrentFieldLocally("trnsRu", fields.trnsRu, currentWord);
        } catch (error) {
            runInAction(() => {
                this.uiStatus.perProcess[processIndex].message = {
                    type: "error",
                    text: `Error adding additional information - ${error} - ${error?.response?.data}`
                };
            });
        }
    }

    async saveArrayOfInfo(field, newInfo, currentWord, dictionaryType, processIndex) {
        try {
            const isArrayField = [
                "distractingForTrns",
                "distractingForOriginal",
                "distractingForDefinitionInEng",
                "distractingForDefinitionInHeb",
                "distractingForWordFamilies",
                "sentencesWithMissing",
                "sentencesWithLastMissing",
                "opposites",
                "synonyms"
            ].includes(field);

            if (!isArrayField || !Array.isArray(newInfo)) return;

            const fieldMap = {
                distractingForTrns: "distrForTrns",
                distractingForOriginal: "distrForOriginal",
                distractingForDefinitionInEng: "distrForDefinitionInEng",
                distractingForDefinitionInHeb: "distrForDefinitionInHeb",
                distractingForWordFamilies: "distrForWordFamilies",
                sentencesWithMissing: "sentencesMissing",
                sentencesWithLastMissing: "sentencesLastMissing",
                opposites: "opposite",
                synonyms: "synonym"
            };

            const fieldName = fieldMap[field];
            if (!fieldName) return;

            while (newInfo.length > 0) {
                const value = newInfo.pop();

                await this.saveInfo({
                    fields: { [fieldName]: value },
                    currentWord,
                    dictionaryType,
                    processIndex
                });
            }

        } catch (error) {
            runInAction(() => {
                this.uiStatus.perProcess[processIndex].message = {
                    type: "error",
                    text: `Error adding all additional information - ${error}`
                };
            });
        }
    }

    cancelCreation() {
        runInAction(() => {
            this.isCanceled = true;
        });
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

const additionalInfo_parallelCreation = new AdditionalInfo_parallelCreation();
export default additionalInfo_parallelCreation;
